import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Share2 } from 'lucide-react';
import {
  BackofficePageModeActionMenu,
  copyTextToClipboard,
  type BackofficePageMode,
  type TableActionItem,
} from '@/components/shared';

export interface MessageBusPageActionsMenuProps {
  pageMode: BackofficePageMode;
  pageLabel: string;
  onPageModeChange: (pageMode: BackofficePageMode) => void;
  onRefresh: () => void;
  refreshing?: boolean;
}

export function MessageBusPageActionsMenu({
  pageMode,
  pageLabel,
  onPageModeChange,
  onRefresh,
  refreshing = false,
}: MessageBusPageActionsMenuProps) {
  const [shareLabel, setShareLabel] = useState(`Share ${pageLabel}`);

  useEffect(() => {
    const baseLabel = `Share ${pageLabel}`;
    if (shareLabel === baseLabel) {
      return;
    }

    const timeout = window.setTimeout(() => setShareLabel(baseLabel), 2_000);
    return () => window.clearTimeout(timeout);
  }, [pageLabel, shareLabel]);

  const fixedInlineActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'refresh',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        placement: 'fixed-inline',
        isLoading: refreshing,
        onSelect: onRefresh,
      },
    ],
    [onRefresh, refreshing]
  );

  const viewActions = useMemo<TableActionItem[]>(
    () => [
      {
        id: 'share',
        label: shareLabel,
        icon: Share2,
        placement: 'menu',
        onSelect: () => {
          const shareUrl = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
          void copyTextToClipboard(shareUrl).then((result) => {
            setShareLabel(
              result === 'copied' ? `${pageLabel} link copied!` : `Could not copy ${pageLabel} link`
            );
          });
        },
      },
    ],
    [pageLabel, shareLabel]
  );

  return (
    <BackofficePageModeActionMenu
      pageMode={pageMode}
      onPageModeChange={onPageModeChange}
      fixedInlineActions={fixedInlineActions}
      viewActions={viewActions}
      editActionLabel="Edit layout"
      label={`${pageLabel} actions`}
    />
  );
}
